import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../context/LanguageContext';
import { track, ANALYTICS_EVENTS } from '../lib/analytics';
import TechGrid from './TechGrid';

const GLOW_SIZE = 560;

/**
 * Cursor-follow spotlight. Mutates the DOM node's transform directly (no
 * React state/re-render per mousemove) and is rAF-throttled, so it's
 * essentially free on the main thread. Skipped entirely on touch devices
 * and when the user prefers reduced motion.
 */
const CursorGlow = ({ containerRef }) => {
  const glowRef = useRef(null);
  const frameRef = useRef(null);
  const targetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const container = containerRef.current;
    const glow = glowRef.current;
    if (!container || !glow) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const applyTransform = () => {
      glow.style.transform = `translate3d(${targetRef.current.x - GLOW_SIZE / 2}px, ${targetRef.current.y - GLOW_SIZE / 2}px, 0)`;
      frameRef.current = null;
    };

    const handleMove = (e) => {
      const rect = container.getBoundingClientRect();
      targetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(applyTransform);
      }
    };

    const handleEnter = () => { glow.style.opacity = '1'; };
    const handleLeave = () => { glow.style.opacity = '0'; };

    container.addEventListener('mousemove', handleMove);
    container.addEventListener('mouseenter', handleEnter);
    container.addEventListener('mouseleave', handleLeave);

    return () => {
      container.removeEventListener('mousemove', handleMove);
      container.removeEventListener('mouseenter', handleEnter);
      container.removeEventListener('mouseleave', handleLeave);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [containerRef]);

  return (
    <div
      ref={glowRef}
      aria-hidden="true"
      className="absolute left-0 top-0 rounded-full pointer-events-none opacity-0 transition-opacity duration-300"
      style={{
        width: GLOW_SIZE,
        height: GLOW_SIZE,
        background: 'radial-gradient(circle, rgba(139,124,255,0.16) 0%, rgba(139,124,255,0) 70%)',
        willChange: 'transform',
      }}
    />
  );
};

const SynapsisHero = () => {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  const scrollToContact = () => {
    const el = document.getElementById('contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSolutions = () => {
    const el = document.getElementById('solutions');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stats = [
    { number: t('hero.stat.number1'), label: t('hero.stat.website') },
    { number: t('hero.stat.number2'), label: t('hero.stat.companySystem') },
    { number: t('hero.stat.number3'), label: t('hero.stat.automatization') },
    { number: t('hero.stat.number4'), label: t('hero.stat.digitalTransformation') },
  ];

  const whatsappPhone = '34627043397';

  return (
    <div ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <TechGrid />
        <CursorGlow containerRef={containerRef} />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 z-10 pt-24 pb-16 md:pt-0 md:pb-0">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full surface mb-8 text-sm text-gray-300"
          >
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
            <span className="eyebrow text-xs text-gray-300">{t('hero.badge')}</span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6"
          >
            <span className="text-ink">{t('hero.titleLine1')}</span>
            <br />
            <span className="text-accent-400">
              {t('hero.titleLine2')}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-base md:text-lg text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 md:mb-20"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="px-8 py-3.5 bg-accent-500 text-white rounded-full font-semibold flex items-center gap-2 shadow-glow-accent hover:bg-accent-600 transition-colors"
              onClick={() => { track(ANALYTICS_EVENTS.CTA_CLICKED, { location: 'hero' }); scrollToContact(); }}
            >
              {t('hero.button.start')}
              <ArrowRightIcon className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="px-8 py-3.5 surface text-white rounded-full font-medium flex items-center gap-2 hover:border-accent-500/40 hover:shadow-glow-accent transition-all"
              onClick={() => { track(ANALYTICS_EVENTS.CTA_CLICKED, { location: 'hero_secondary' }); scrollToSolutions(); }}
            >
              {t('hero.button.solutions')}
              <ChevronRightIcon className="w-4 h-4" />
            </motion.button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
          >
            {stats.map((stat, index) => {
              const whatsappMessage = encodeURIComponent(`${t('hero.stat.consulting')} ${stat.number}. ${t('hero.stat.consultingSecondPart')}`);
              const whatsappUrl = `https://wa.me/${whatsappPhone}?text=${whatsappMessage}`;
              return (
                <motion.a
                  key={index}
                  onClick={() => track(ANALYTICS_EVENTS.WHATSAPP_CLICKED, { topic: stat.number })}
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                  className="surface rounded-2xl p-4 md:p-5 cursor-pointer block group hover:border-accent-500/40 transition-colors duration-300"
                >
                  <div className="font-mono-num text-sm md:text-base font-semibold text-accent-400 mb-1">
                    {stat.number}
                  </div>
                  <div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                    {stat.label}
                  </div>
                </motion.a>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      {isDesktop && (
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          aria-hidden="true"
        >
          <div className="w-5 h-8 border border-white/20 rounded-full flex justify-center">
            <div className="w-0.5 h-2 bg-white/30 rounded-full mt-1.5" />
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default SynapsisHero;
